import React, { useState } from "react";
import { validateLoggedIn, getSession } from "../utils";
import {
    TextField, Paper, Typography, Box, Grid, Button, CircularProgress, Alert, MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import NavBar from "../navbar";
import config from "../config";
import * as Sentry from "@sentry/react";

const CreateEndpoint = () => {
    const navigate = useNavigate();
    const [data, setData] = useState({
        path: "",
        method: "post",
        price: 1,
        ten_second_limit: null,
        minutely_limit: null,
        hourly_limit: null,
        daily_limit: null,
        monthly_limit: null,
        increase_every: null,
        enabled: true,
        hidden_from_docs: false,
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const validate_logged_in = validateLoggedIn();
    if (validate_logged_in) return validate_logged_in;

    const handleChange = (field, value) => setData({ ...data, [field]: value });

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);
            if (!data.path.trim()) { setError("Path is required"); setSaving(false); return; }

            const response = await fetch(config.apiBaseUrl + "/admin/endpoints", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": getSession().token,
                },
                body: JSON.stringify({
                    path: data.path,
                    method: data.method,
                    price: parseInt(data.price),
                    ten_second_limit: data.ten_second_limit,
                    minutely_limit: data.minutely_limit,
                    hourly_limit: data.hourly_limit,
                    daily_limit: data.daily_limit,
                    monthly_limit: data.monthly_limit,
                    increase_every: data.increase_every !== null && data.increase_every !== "" ? parseInt(data.increase_every) : null,
                    enabled: data.enabled,
                    hidden_from_docs: data.hidden_from_docs,
                }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Error: ${response.status}`);
            }
            setSuccess(true);
            setTimeout(() => navigate("/endpoints"), 1500);
        } catch (err) {
            Sentry.captureException(err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const limitField = (label, field) => (
        <Grid item xs={12} md={4} key={field}>
            <TextField fullWidth margin="normal" label={label} type="number" inputProps={{ min: 1 }}
                value={data[field] === null ? "" : data[field]}
                onChange={(e) => handleChange(field, e.target.value === "" ? null : parseInt(e.target.value) || null)}
                disabled={saving} helperText="Empty = no limit" />
        </Grid>
    );

    return (
        <>
            <NavBar />
            <Box sx={{ maxWidth: 800, margin: "auto", padding: 3 }}>
                <Paper sx={{ padding: 3 }}>
                    <Typography variant="h5" gutterBottom>Create Endpoint</Typography>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>Endpoint created successfully!</Alert>}
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth required margin="normal" label="Path" value={data.path} onChange={(e) => handleChange("path", e.target.value)} disabled={saving} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField select fullWidth margin="normal" label="Method" value={data.method} onChange={(e) => handleChange("method", e.target.value)} disabled={saving}>
                                <MenuItem value="get">GET</MenuItem>
                                <MenuItem value="post">POST</MenuItem>
                                <MenuItem value="put">PUT</MenuItem>
                                <MenuItem value="delete">DELETE</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth margin="normal" label="Price" type="number" inputProps={{ min: 1 }} value={data.price} onChange={(e) => handleChange("price", parseInt(e.target.value) || 1)} disabled={saving} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth margin="normal" label="Increase Every" type="number" value={data.increase_every === null ? "" : data.increase_every} onChange={(e) => handleChange("increase_every", e.target.value === "" ? null : parseInt(e.target.value) || null)} disabled={saving} helperText="Leave empty for none" />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField select fullWidth margin="normal" label="Enabled" value={data.enabled ? "true" : "false"} onChange={(e) => handleChange("enabled", e.target.value === "true")} disabled={saving}>
                                <MenuItem value="true">Yes</MenuItem>
                                <MenuItem value="false">No</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField select fullWidth margin="normal" label="Hidden From Docs" value={data.hidden_from_docs ? "true" : "false"} onChange={(e) => handleChange("hidden_from_docs", e.target.value === "true")} disabled={saving} helperText="Callable and billed as usual, but absent from the dashboard API docs">
                                <MenuItem value="true">Yes</MenuItem>
                                <MenuItem value="false">No</MenuItem>
                            </TextField>
                        </Grid>
                        {limitField("10s Limit", "ten_second_limit")}
                        {limitField("Minutely Limit", "minutely_limit")}
                        {limitField("Hourly Limit", "hourly_limit")}
                        {limitField("Daily Limit", "daily_limit")}
                        {limitField("Monthly Limit", "monthly_limit")}
                    </Grid>
                    <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving} startIcon={saving && <CircularProgress size={16} />}>
                            {saving ? "Saving..." : "Create Endpoint"}
                        </Button>
                        <Button variant="outlined" color="error" onClick={() => navigate("/endpoints")} disabled={saving}>Cancel</Button>
                    </Box>
                </Paper>
            </Box>
        </>
    );
};

export default CreateEndpoint;
